'use client';

import { useAudioPlayerStore } from '@/client/stores/useAudioPlayerStore';
import { AudioPlayerPlayPauseButton } from '@/components/ui/audio-player/audio-player-play-pause-button';
import { AudioPlayerStopButton } from '@/components/ui/audio-player/audio-player-stop-button';
import { TimeDisplay } from '@/components/ui/audio-player/audio-player-time-display';
import { TrackInfoDisplay } from '@/components/ui/audio-player/audio-player-track-info-display';
import { AudioPlayerVolumeControl } from '@/components/ui/audio-player/audio-player-volume-control';
import { useTheme } from 'next-themes';
import React, { useCallback, useEffect, useRef, useState } from 'react';

type ColorOption = string | number | { light: string | number; dark: string | number };

interface AudioPlayerProps {
  waveformColor?: ColorOption;
  backgroundColor?: ColorOption;
  smoothingFactor?: number;
  waveformHeightFactor?: number;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  waveformColor = {
    light: 'oklch(0.18 0.01 244.62)',
    dark: 'oklch(0.87 0 0)',
  },
  backgroundColor = {
    light: 'oklch(0.87 0 0)',
    dark: 'oklch(0.18 0.01 244.62)',
  },
  smoothingFactor = 0.85,
  waveformHeightFactor = 0.4,
}) => {
  const { theme } = useTheme();
  const { track, player } = useAudioPlayerStore();

  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const [audioElementIsSet, markAudioElementIsSet] = useState<boolean>(false);

  // Update Canvas Size - debounced for performance - extracted for clarity
  const updateCanvasSize = useCallback(() => {
    if (!containerRef.current || !canvasRef.current) return;
    const width = containerRef.current.offsetWidth;
    const height = containerRef.current.offsetHeight;
    setCanvasSize(() => ({ width, height }));
    const timerId = setTimeout(() => {
      player.setCanvasWidth(width);
      player.setCanvasHeight(height);
    }, 100);

    return () => clearTimeout(timerId);
  }, [player]);

  useEffect(() => {
    if (!window) return;

    const initializeAudioContext = () => {
      if (!audioElementIsSet) {
        player.setAudioContext(new AudioContext());
        markAudioElementIsSet(true);
      }
    };

    window.addEventListener('click', initializeAudioContext);
    return () => window.removeEventListener('click', initializeAudioContext);
  }, [audioElementIsSet, player]);

  useEffect(() => {
    player.setCanvas(canvasRef.current);
  }, [player, canvasRef]);

  useEffect(() => {
    player.setTrack(track);
  }, [player, track]);

  useEffect(() => {
    player.setAudioElement(audioRef.current);
  }, [player, audioRef]);

  useEffect(() => {
    player.setSmoothingFactor(smoothingFactor);
  }, [player, smoothingFactor]);

  useEffect(() => {
    player.setWaveformHeightFactor(waveformHeightFactor);
  }, [player, waveformHeightFactor]);

  // Update Canvas Size Effect with ResizeObserver
  useEffect(() => {
    let timerId: NodeJS.Timeout;

    const resizeObserver = new ResizeObserver(() => {
      if (timerId) {
        clearTimeout(timerId);
      }
      timerId = setTimeout(() => {
        updateCanvasSize();
      }, 300);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [updateCanvasSize]);

  useEffect(() => {
    player.setWaveformColor(parseColorOptions(waveformColor, theme));
    player.setPlayedWaveformColor(parseColorOptions(waveformColor, theme));
    player.setBackgroundColor(parseColorOptions(backgroundColor, theme));
  }, [backgroundColor, player, theme, waveformColor]);

  return (
    <div className="flex items-center h-12 font-mono md:justify-between">
      <audio ref={audioRef} preload={'metadata'} />
      <TrackInfoDisplay className={'text-xs mx-auto sm:text-base order-2 md:order-none md:mx-0 md:basis-2/5'} />
      <AudioPlayerPlayPauseButton className={'w-10 text-xs flex justify-start order-1 sm:w-16 sm:text-base md:order-none md:justify-center'} />
      <AudioPlayerStopButton className={'w-10 text-xs flex justify-end order-3 sm:w-16 sm:text-base md:order-none md:justify-center'} />
      <TimeDisplay className={'hidden w-12 text-center md:block'} />
      <div className="hidden md:block md:basis-1/4 h-12">
        <div ref={containerRef} className="relative w-full h-full">
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            className="absolute top-0 left-0 cursor-pointer"
            aria-label="Audio Waveform - Click to Seek"
            role="button"
            tabIndex={0}
          />
        </div>
      </div>
      <AudioPlayerVolumeControl className={'hidden md:block'} audioRef={audioRef} />
    </div>
  );
};

export default AudioPlayer;

function parseColorOptions(opt: ColorOption, theme?: string) {
  if (typeof opt === 'object') return String(theme === 'dark' ? opt.dark : opt.light);
  return opt.toString();
}
